// Guardar la forma de pago desde Forma de pago o Score: mutación, avisos de Promociones, errores y
// paso siguiente. Ambos pasos siguen el mismo camino para que contado se comporte igual.
import { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { toast } from 'sonner';
import {
    CREDIT_IN_PROGRESS_OTHER_STORE_MESSAGE,
    isCreditInProgressOtherStore,
} from '../cashOnlyPayment';
import { paymentMethodFailure } from '../externalReferral';
import { INTEGRATION_ACTION_LABEL, integrationFailure } from '../integrationErrors';
import { SaleChangeBlockedError, type OnSaleChanged } from '../promotionSlots';
import { useSetPaymentMethod, useWizardSession } from './useWizard';
import { wizardErrorMsg } from './wizardError';

/** Las dos pistas que se guardan como forma de pago. La derivación externa no es ninguna. */
export type PaymentTrack = 'SUREKEEP' | 'CASH';

export type PaymentMethodNextStep = 'payment' | 'quote' | 'score';

type WizardStepSlug = PaymentMethodNextStep | 'build-sale' | 'product';

/** Lo que Promociones soltó con la nueva forma de pago; el vendedor sigue cuando lo vio. */
export interface DroppedPromotions {
    notices: string[];
    next: PaymentMethodNextStep;
}

export interface PaymentMethodChangeOptions {
    onSaleChanged?: OnSaleChanged;
    /** El silo pide pasar a contado (falla de integración con salida por contado). */
    onCashRecovery?: () => void;
}

export function usePaymentMethodChange(
    sessionId: string,
    { onSaleChanged, onCashRecovery }: PaymentMethodChangeOptions = {},
) {
    const navigate = useNavigate();
    const { data: session, refresh } = useWizardSession(sessionId);
    const setMethod = useSetPaymentMethod(sessionId);
    // Al pasar a contado se espera a que Promociones suelte lo que solo aplica con SureKeep.
    const [releasing, setReleasing] = useState(false);
    const [dropped, setDropped] = useState<DroppedPromotions | null>(null);

    const goTo = async (step: WizardStepSlug): Promise<void> => {
        await navigate({ to: '/wizard/s/$sessionId/$step', params: { sessionId, step } });
    };

    /** Promociones revisa lo apartado; si no pudo, avisa con reintento y devuelve `null`. */
    const notifyPaymentMethodChanged = async (
        sureKeep: boolean,
        retry: () => void,
    ): Promise<string[] | null> => {
        setReleasing(!sureKeep);
        try {
            const outcome = await onSaleChanged?.({ kind: 'paymentMethod', sureKeep });
            return outcome ? outcome.notices : [];
        } catch (error) {
            if (!(error instanceof SaleChangeBlockedError)) return [];
            toast.error(error.message, { action: { label: 'Reintentar', onClick: retry } });
            return null;
        } finally {
            setReleasing(false);
        }
    };

    const handleFailure = async (e: unknown): Promise<void> => {
        if (isCreditInProgressOtherStore(e)) {
            toast.error(CREDIT_IN_PROGRESS_OTHER_STORE_MESSAGE);
            void refresh();
            return;
        }
        const integration = integrationFailure(e);
        if (integration?.recovery === 'cash') {
            toast.error(integration.message, {
                action: { label: INTEGRATION_ACTION_LABEL.cash, onClick: () => onCashRecovery?.() },
            });
            return;
        }
        if (integration) toast.error(integration.message);
        if (integration?.recovery === 'build-sale') {
            void refresh();
            await goTo('build-sale');
            return;
        }
        if (integration) return;
        const failure = paymentMethodFailure(e, session);
        if (failure) {
            if (failure.message !== undefined) toast.error(failure.message);
            await goTo(failure.step);
            return;
        }
        toast.error(wizardErrorMsg(e, 'No se pudo guardar la forma de pago — intenta de nuevo.'));
    };

    /** Guarda la pista y avanza; con avisos de Promociones se queda para que el vendedor los vea. */
    const change = async (track: PaymentTrack, retry: () => void): Promise<void> => {
        setDropped(null);
        const sureKeep = track === 'SUREKEEP';
        try {
            await setMethod.mutateAsync(sureKeep ? 'SUREKEEP' : 'CASH_OR_CARD');
            const notices = await notifyPaymentMethodChanged(sureKeep, retry);
            if (notices === null) return;
            // Con nivel ya resuelto el score no se repite: SureKeep va directo a cotizar.
            const next: PaymentMethodNextStep = !sureKeep
                ? 'payment'
                : session?.loan?.level
                  ? 'quote'
                  : 'score';
            if (notices.length > 0) {
                setDropped({ notices, next });
                return;
            }
            await goTo(next);
        } catch (e) {
            await handleFailure(e);
        }
    };

    return {
        change,
        goTo,
        dropped,
        clearDropped: () => {
            setDropped(null);
        },
        busy: setMethod.isPending || releasing,
    };
}
